import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Permission, UserRole } from '@manpower/shared';
import { UserRole as Role } from '@manpower/shared';
import { PageSpinner } from '@/components/primitives';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  roles?: UserRole[];
  permissions?: Permission[];
  /** Wraps a single element; omit to gate a whole nested branch via <Outlet />. */
  children?: ReactNode;
}

/** Where each role lands when it hits a page it is not allowed to see. */
const HOME_FOR_ROLE: Record<UserRole, string> = {
  SUPER_ADMIN: '/admin',
  ADMIN: '/admin',
  STAFF: '/admin',
  EMPLOYER: '/employer',
  SEEKER: '/seeker',
};

/**
 * Route gate. This is a usability guard, not a security boundary — every
 * endpoint re-checks the role and permissions server-side, so a user who edits
 * their way past this sees an empty page, not data.
 */
export function ProtectedRoute({ roles, permissions, children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <PageSpinner label="Checking your session…" />;

  if (!user) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (roles?.length && !roles.includes(user.role)) {
    return <Navigate to={HOME_FOR_ROLE[user.role]} replace />;
  }

  if (
    permissions?.length &&
    user.role !== Role.SUPER_ADMIN &&
    !permissions.every((p) => user.permissions.includes(p))
  ) {
    return <Navigate to={HOME_FOR_ROLE[user.role]} replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
